import { useState, useEffect } from "react";
import { validateForm } from "../utils/formValidation/validation";
import { addressRules } from "../utils/formValidation/validationRules";
import TextFieldCustom from "./elements/textfieldCustom";
import CustomButton from "./elements/CustomButton/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import MapPickerPopup from "./MapPicker";
import { buildAddressPayload } from "../utils/latLongFetch";
import { useSnackbar } from "./SnackbarContext";
import MapComponent from "./mapComponent";

function AddressForm({ onClose, mode, addressDetails, onSubmit }) {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const showSnackbar = useSnackbar();
  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
    is_primary: false,
    coordinates: null,
    address_type: "Home",
  });

  const [formErrors, setFormErrors] = useState({});
  const [mapOpen, setMapOpen] = useState(false); // map popup state
  const [originalZip, setOriginalZip] = useState(null);
  useEffect(() => {
    if (addressDetails) {
      setFormData({
        addressLine1: addressDetails?.house_no,
        addressLine2: addressDetails?.street_address,
        landmark: addressDetails?.landmark,
        city: addressDetails?.city,
        state: addressDetails?.state,
        zip: addressDetails?.zipcode,
        is_primary: addressDetails?.is_primary,
        coordinates: null,
        address_type: addressDetails?.address_type,
      });
      setOriginalZip(addressDetails?.zipcode);
    }
  }, [addressDetails]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async () => {
    const errors = validateForm(formData, addressRules);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      let addressPayload = await buildAddressPayload({
        formData,
        mode,
        originalZip,
        addressDetails,
        showSnackbar,
      });

      if (!addressPayload) return;
      addressPayload = {
        ...addressPayload,
        address_type: formData.address_type,
      };
      if (mode === "edit") {
        addressPayload = { ...addressPayload, id: addressDetails.id };
      }
      onSubmit(addressPayload);
    }
  };

  const handleMapSelect = (latLng) => {
    setFormData((prev) => ({ ...prev, coordinates: latLng }));
    setMapOpen(false);
  };

  return (
    <div className="pt-6 px-10">
      <div className="flex justify-between mb-6">
        <div className="text-lg font-[500]">
          {mode === "edit" ? "Edit Address" : "Create Address"}
        </div>
        <button
          className="text-gray-600 hover:text-black"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="overflow-y-auto pb-3 scrollbar-hide max-h-[450px]">
        <div className="mt-2">
          <TextFieldCustom
            label="Address*"
            fullWidth
            value={formData.addressLine1}
            onChange={handleChange("addressLine1")}
            error={!!formErrors.addressLine1}
            helperText={formErrors.addressLine1 || " "}
            heightClass="custom-textarea-height"
            multiline
            rows={2}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-3 items-stretch">
          <div className="sm:mb-3">
            <TextFieldCustom
            label="Unit"
            fullWidth
            value={formData.landmark}
            onChange={handleChange("landmark")}
          />
          </div>

          <TextFieldCustom
            label="City/Town*"
            fullWidth
            value={formData.city}
            onChange={handleChange("city")}
            error={!!formErrors.city}
            helperText={formErrors.city || " "}
          />

          <TextFieldCustom
            label="State*"
            fullWidth
            value={formData.state}
            onChange={handleChange("state")}
            error={!!formErrors.state}
            helperText={formErrors.state || " "}
          />

          <TextFieldCustom
            label="Zip code*"
            fullWidth
            value={formData.zip}
            onChange={handleChange("zip")}
            error={!!formErrors.zip}
            helperText={formErrors.zip || " "}
          />
        </div>
        <div className="mt-2">
          <div className="text-sm font-medium mb-2">Address Type</div>
          <div className="flex gap-2">
            {["Home", "Office", "Other"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, address_type: type }))
                }
                className={`px-3 py-1 rounded-full border text-sm ${
                  formData.address_type === type
                    ? "bg-[#3383B2] text-white border-[#3383B2]"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <CustomButton
            // onClick={() => setMapOpen(true)}
            onClick={() => setIsMapOpen(true)}
            customeClass="primary-custom-button w-full sm:w-[200px] h-[40px]"
          >
            Locate on Map
          </CustomButton>
        </div>

        {isMapOpen && (
          <div className="mt-3">
            <MapComponent
              isOpen={isMapOpen}
              handleClose={() => setIsMapOpen(false)}
              address={`${formData.addressLine1}, ${formData.city}, ${formData.state}`}
              zipCode={formData.zip}
              onSelect={(coords) => handleMapSelect(coords)}
              height="250px"
            />
          </div>
        )}
      </div>
      <div className="flex justify-center items-center mt-4 mb-6 gap-3">
        <CustomButton
          onClick={onClose}
          customeClass="customer-light-button w-[150px] h-[40px]"
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={handleSubmit}
          customeClass="customer-dark-button w-[150px] h-[40px]"
        >
          {mode === "edit" ? "Update" : "Create"}
        </CustomButton>
      </div>

      <MapPickerPopup
        isOpen={mapOpen}
        handleClose={() => setMapOpen(false)}
        address={`${formData.addressLine1}, ${formData.addressLine2} ${formData.city}`}
        zipCode={formData.zip}
        onSelect={handleMapSelect}
      />
    </div>
  );
}

export default AddressForm;
